import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Grid,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  TextField,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Icon,
  Tooltip,
  IconButton,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BugReportIcon from "@mui/icons-material/BugReport";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";

import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";

import { get, post, postFile, postUploadNoResponse } from "utils/WebService";

function MalaDiretaLote() {
  const { userPerfils } = useUser();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const isLiberado = userPerfils?.some(p => {
    const n = p.ccNome?.toLowerCase() || '';
    return n.includes("gestor") || n.includes("admin") || n.includes("coord");
  });
  const hideFaseFilter = !isLiberado;

  const faseOptions = [
    { id: null, nome: "Ambas" },
    { id: false, nome: "Extrajudicial" },
    { id: true, nome: "Jurídica" }
  ];

  const tableState = useRef({
    pageSize: 10,
    pageIndex: 0,
    sorting: [],
    filtered: [],
    status: [],
    idUsuario: null,
    fase: null,
    hideDevolvidos: false,
    comEmail: false,
    cliente: [],
    saldoInicial: "",
    saldoFinal: "",
  });

  const [data, setData] = useState([]);
  const [pages, setPages] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);

  const [snackbarConfig, setSnackbarConfig] = useState({ open: false, color: "info", icon: "info", title: "", content: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: () => {} });

  const showSnackbar = (color, icon, title, content) => {
    setSnackbarConfig({ open: true, color, icon, title, content });
  };

  const [filters, setFilters] = useState({
    status: [],
    idUsuario: null,
    fase: faseOptions[0],
    hideDevolvidos: false,
    cliente: [],
    comEmail: false,
    saldoInicial: "",
    saldoFinal: "",
  });

  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const searchTimeout = useRef(null);
  
  const [statusFichaOptions, setStatusFichaOptions] = useState([]);
  const [usuariosOptions, setUsuariosOptions] = useState([]);

  const [modelos, setModelos] = useState([]);
  const [contas, setContas] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  
  const [selectedModelo, setSelectedModelo] = useState("");
  const [selectedConta, setSelectedConta] = useState("");
  const [selectedCampanha, setSelectedCampanha] = useState("");
  const [dataRetorno, setDataRetorno] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [emailTeste, setEmailTeste] = useState("");
  const [runningBatchId, setRunningBatchId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [batchStatusData, setBatchStatusData] = useState(null);

  useEffect(() => {
    let intervalId = null;
    if (runningBatchId) {
      intervalId = setInterval(async () => {
        try {
          const res = await get(`/mala-direta/status-lote/${runningBatchId}`);
          setBatchStatusData(res);
          if (res && (res.status === "CONCLUIDO" || res.status === "CANCELADO")) {
            setRunningBatchId(null);
            if (res.status === "CONCLUIDO") {
              showSnackbar("success", "check", "Lote Concluído", `Disparo finalizado: ${res.enviadosSucesso} enviados, ${res.erros} erros.`);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [runningBatchId]);

  const handleSearchPessoas = (val) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        setLoadingPessoas(true);
        const res = await get(`/pessoa/list-all?search=${val || ""}`);
        if (res) {
          setPessoaOptions(res.list || (Array.isArray(res) ? res : []));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingPessoas(false);
      }
    }, 500);
  };

  useEffect(() => {
    handleSearchPessoas("");
    get("/status-ficha/list-all").then((res) => {
      setStatusFichaOptions(res.list || res.data || (Array.isArray(res) ? res : []));
    }).catch(() => {});

    get(`/users/list-all-operators`).then((res) => {
      setUsuariosOptions(res.list || res.data || (Array.isArray(res) ? res : []));
    }).catch(() => {});

    // Fetch lists
    get("/mala-direta/modelos").then(res => setModelos(res || [])).catch(() => {});
    get("/mala-direta/contas").then(res => setContas(res || [])).catch(() => {});
    get("/mala-direta/campanhas").then(res => setCampanhas(res || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, status, idUsuario, fase, hideDevolvidos, cliente, comEmail, saldoInicial, saldoFinal }) => {
    setLoadingTable(true);
    const updatedTableState = {
      pageSize: pageSize ? pageSize : tableState.current.pageSize,
      pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
      sorting: sorting ? sorting : tableState.current.sorting,
      filtered: filtered ? filtered : tableState.current.filtered,
      status: status !== undefined ? status : tableState.current.status,
      idUsuario: idUsuario !== undefined ? idUsuario : tableState.current.idUsuario,
      fase: fase !== undefined ? fase : tableState.current.fase,
      hideDevolvidos: hideDevolvidos !== undefined ? hideDevolvidos : tableState.current.hideDevolvidos,
      cliente: cliente !== undefined ? cliente : tableState.current.cliente,
      comEmail: comEmail !== undefined ? comEmail : tableState.current.comEmail,
      saldoInicial: saldoInicial !== undefined ? saldoInicial : tableState.current.saldoInicial,
      saldoFinal: saldoFinal !== undefined ? saldoFinal : tableState.current.saldoFinal,
    };

    tableState.current = updatedTableState;

    post("/devedor/list", getPageablePayload(updatedTableState)).then(({ data, pages, recordsTotal }) => {
      setData(data || []);
      setPages(pages || 0);
      setRecordsTotal(recordsTotal || 0);
      setLoadingTable(false);
    }).catch(() => {
      setLoadingTable(false);
    });
  }, []);

  useEffect(() => {
    fetchData(tableState.current);
  }, [fetchData]);

  const getPageablePayload = (state) => {
    return {
      page: state.pageIndex || 0,
      pageSize: state.pageSize || 10,
      sorted: state.sorting || [],
      idStatusFicha: state.status?.length === 1 ? state.status[0].id : null,
      idsStatusFicha: state.status?.length > 1 ? state.status.map(s => s.id) : null,
      idUsuario: state.idUsuario,
      hideDevolvidos: state.hideDevolvidos,
      idPessoa: state.cliente?.length === 1 ? state.cliente[0].id : null,
      idsPessoas: state.cliente?.length > 1 ? state.cliente.map(c => c.id) : null,
      comEmail: state.comEmail,
      saldoInicial: state.saldoInicial ? parseFloat(state.saldoInicial) : null,
      saldoFinal: state.saldoFinal ? parseFloat(state.saldoFinal) : null,
      isJuridico: state.fase !== undefined ? state.fase : null,
    };
  };

  const handleApplyFilter = () => {
    const updatedTableState = {
      ...tableState.current,
      status: filters.status,
      idUsuario: filters.idUsuario ? filters.idUsuario.id : null,
      fase: filters.fase ? filters.fase.id : null,
      hideDevolvidos: filters.hideDevolvidos,
      cliente: filters.cliente,
      comEmail: filters.comEmail,
      saldoInicial: filters.saldoInicial,
      saldoFinal: filters.saldoFinal,
      pageIndex: 0,
    };
    tableState.current = updatedTableState;
    fetchData(updatedTableState);
  };

  const handleCleanFilter = () => {
    setFilters({
      status: [],
      idUsuario: null,
      fase: faseOptions[0],
      hideDevolvidos: false,
      cliente: [],
      comEmail: false,
      saldoInicial: "",
      saldoFinal: "",
    });
    const updatedTableState = {
      ...tableState.current,
      status: [],
      idUsuario: null,
      fase: faseOptions[0].id,
      hideDevolvidos: false,
      cliente: [],
      comEmail: false,
      saldoInicial: "",
      saldoFinal: "",
      pageIndex: 0,
    };
    tableState.current = updatedTableState;
    fetchData(updatedTableState);
  };

  const getPayload = () => {
    // For sending, we want to fetch all matches, not just the first 10 pages
    const pageable = getPageablePayload(tableState.current);
    pageable.pageSize = 99999;
    
    return {
      modeloId: selectedModelo,
      contaEmailId: selectedConta || null,
      pageable: pageable,
      parametros: {
        campanhaId: selectedCampanha || null,
        dataRetorno: dataRetorno || null,
        testEmail: emailTeste || null
      }
    };
  };

  const handleValidacao = async () => {
    try {
      await postFile("/mala-direta/validacao", getPageablePayload(tableState.current), "validacao_mala_direta.xlsx");
    } catch (err) {
      console.error("Erro ao gerar validação", err);
      showSnackbar("error", "warning", "Erro", "Erro ao baixar validação.");
    }
  };

  const handleEnviarLote = async () => {
    if (!selectedModelo) {
      showSnackbar("warning", "warning", "Atenção", "Selecione um modelo de carta.");
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Confirmar Envio",
      message: `Deseja realmente iniciar o envio em lote para os ${recordsTotal} casos filtrados?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        try {
          const payload = getPayload();

          if (attachment) {
            const formData = new FormData();
            formData.append("anexo", attachment);
            formData.append("payload", JSON.stringify(payload));
            const response = await postUploadNoResponse("/mala-direta/enviar-lote-anexo", formData);
            const data = await response.json();
            if (data.batchId) setRunningBatchId(data.batchId);
            showSnackbar("success", "check", "Sucesso", data.message || "Lote enviado para processamento com anexo!");
          } else {
            const response = await post("/mala-direta/enviar-lote", payload);
            if (response.batchId) setRunningBatchId(response.batchId);
            showSnackbar("success", "check", "Sucesso", response.message || "Lote em processamento.");
          }
        } catch (err) {
          console.error(err);
          showSnackbar("error", "warning", "Erro", "Erro ao enviar lote.");
        }
      }
    });
  };

  const handleCancelBatch = async () => {
    if (!runningBatchId) return;
    try {
      const response = await post(`/mala-direta/cancelar-lote/${runningBatchId}`, {});
      showSnackbar("success", "check", "Sucesso", response.message || "Cancelamento solicitado.");
      setRunningBatchId(null);
      if (batchStatusData) {
        setBatchStatusData({ ...batchStatusData, status: "CANCELADO" });
      }
    } catch (err) {
      console.error(err);
      showSnackbar("error", "warning", "Erro", err.message || "Erro ao cancelar lote.");
    }
  };

  const handlePreview = async () => {
    if (!selectedModelo) {
      showSnackbar("warning", "warning", "Atenção", "Selecione um modelo de carta.");
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      const payload = getPayload();
      const response = await post("/mala-direta/preview", payload);
      setPreviewContent(response.message || "");
    } catch (err) {
      console.error(err);
      setPreviewContent("Erro ao carregar pré-visualização: " + (err.message || ""));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleEnviarTeste = async () => {
    if (!selectedModelo) {
      showSnackbar("warning", "warning", "Atenção", "Selecione um modelo de carta.");
      return;
    }
    if (!emailTeste) {
      showSnackbar("warning", "warning", "Atenção", "Informe um e-mail de teste válido.");
      return;
    }
    try {
      const payload = getPayload();

      if (attachment) {
        const formData = new FormData();
        formData.append("anexo", attachment);
        formData.append("payload", JSON.stringify(payload));
        const response = await postUploadNoResponse("/mala-direta/enviar-teste-anexo", formData);
        const data = await response.json();
        showSnackbar("success", "check", "Sucesso", data.message || "E-mail de teste enviado com anexo!");
      } else {
        const response = await post("/mala-direta/enviar-teste", payload);
        showSnackbar("success", "check", "Sucesso", response.message || "E-mail de teste em processamento.");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("error", "warning", "Erro", err.message || "Erro ao enviar teste.");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachment(e.target.files[0]);
    }
  };

  const userColumns = [
    {
      header: "Cód. Cliente",
      accessorKey: "codigoCliente",
      cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
      isSorted: true
    },
    {
      header: "Cliente",
      accessorKey: "nomeCliente",
      cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
      isSorted: false
    },
    {
      header: "Nome",
      accessorKey: "nome",
      cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
      isSorted: true
    },
    {
      header: "CPF/CNPJ",
      accessorKey: "cpfCnpj",
      cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
      isSorted: true
    },
    {
      header: "Status",
      accessorKey: "statusFichaNome",
      cell: info => {
        const isDevolvido = info.row.original.status === 1;
        const displayValue = info.getValue() || info.row.original.status || "-";
        if (isDevolvido) {
          return (
            <DefaultCell>
              <MDTypography variant="caption" color="error" fontWeight="bold">
                {displayValue} (Devolvido)
              </MDTypography>
            </DefaultCell>
          );
        }
        return <DefaultCell>{displayValue}</DefaultCell>;
      },
      isSorted: true
    }
  ];

  return (
    <Grid container display="flex" justifyContent="center">
      <Grid item xs={12} md={12}>
        <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
          <MDTypography variant="h5" mb={1}>Filtros da Base (Prévia de Envio)</MDTypography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Utilize estes filtros para definir quais devedores receberão o e-mail em lote.
          </Typography>

          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={2}>
              <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Saldo Inicial</InputLabel>
              <MDInput
                size="small"
                placeholder="De..."
                type="number"
                value={filters.saldoInicial ?? ""}
                onChange={(e) => setFilters({ ...filters, saldoInicial: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Saldo Final</InputLabel>
              <MDInput
                size="small"
                placeholder="Até..."
                type="number"
                value={filters.saldoFinal ?? ""}
                onChange={(e) => setFilters({ ...filters, saldoFinal: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
              <MDSelect
                isMulti={true}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.nomeComum || option.nomeCompleto || "";
                }}
                name="cliente"
                placeholder="Buscar cliente..."
                value={filters.cliente || []}
                options={pessoaOptions}
                onChange={(value) => setFilters(prev => ({ ...prev, cliente: value || [] }))}
                onInputChange={(inputValue, { action }) => {
                  if (action === 'input-change') {
                    handleSearchPessoas(inputValue);
                  }
                }}
                isLoading={loadingPessoas}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
              <MDSelect
                isMulti={true}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.ccNome}
                name="status"
                placeholder="Todos"
                value={filters.status || []}
                options={statusFichaOptions}
                onChange={(value) => setFilters({ ...filters, status: value || [] })}
              />
            </Grid>
            {!hideFaseFilter && (
              <Grid item xs={12} sm={3}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Fase</InputLabel>
                <MDSelect
                  isMulti={false}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.nome}
                  name="fase"
                  placeholder="Ambas"
                  value={filters.fase || null}
                  options={faseOptions}
                  onChange={(value) => setFilters({ ...filters, fase: value })}
                />
              </Grid>
            )}
            
            <Grid item xs={12} mt={1}>
              <Grid container spacing={2}>
                <Grid item>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={filters.hideDevolvidos}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFilters({ ...filters, hideDevolvidos: isChecked });
                          const updated = { ...tableState.current, hideDevolvidos: isChecked, pageIndex: 0 };
                          tableState.current = updated;
                          fetchData(updated);
                        }}
                        color="info"
                      />
                    } 
                    label={<MDTypography variant="button" fontWeight="regular" color="text" sx={{ cursor: "pointer", userSelect: "none" }}>Exceto Devolvidos</MDTypography>}
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={filters.comEmail}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFilters({ ...filters, comEmail: isChecked });
                          const updated = { ...tableState.current, comEmail: isChecked, pageIndex: 0 };
                          tableState.current = updated;
                          fetchData(updated);
                        }}
                        color="info"
                      />
                    } 
                    label={<MDTypography variant="button" fontWeight="bold" color="info" sx={{ cursor: "pointer", userSelect: "none" }}>Somente com E-mail Cadastrado</MDTypography>}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} display="flex" alignItems="center" justifyContent="end" pt={1}>
              <MDBox>
                <MDButton
                  size="medium"
                  sx={{ marginRight: 2 }}
                  variant="contained"
                  color="info"
                  onClick={handleCleanFilter}>
                  Limpar filtro
                </MDButton>
                <MDButton 
                  size="medium" 
                  variant="contained" 
                  color="info" 
                  onClick={handleApplyFilter}>
                  Aplicar filtro
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <DataTable
              loading={loadingTable}
              columnData={userColumns}
              data={data}
              fetchData={fetchData}
              totalPages={pages}
              totalRecords={recordsTotal}
              tableState={tableState} 
            />
          </MDBox>
        </Card>

        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5">Disparo de Mala Direta</MDTypography>
            <Divider />
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Modelo de Mala Direta</InputLabel>
                <MDSelect
                  isMulti={false}
                  getOptionValue={(option) => option.cvId}
                  getOptionLabel={(option) => option.ccNome}
                  name="modelo"
                  placeholder="Selecione um modelo..."
                  value={modelos.find(m => m.cvId === selectedModelo) || null}
                  options={modelos}
                  onChange={(option) => setSelectedModelo(option ? option.cvId : "")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Conta Remetente (Opcional)</InputLabel>
                <MDSelect
                  isMulti={false}
                  getOptionValue={(option) => option.cvId}
                  getOptionLabel={(option) => `${option.ccNome} (${option.ccEmail})`}
                  name="conta"
                  placeholder="Padrão do Sistema"
                  value={contas.find(c => c.cvId === selectedConta) || null}
                  options={contas}
                  onChange={(option) => setSelectedConta(option ? option.cvId : "")}
                />
              </Grid>
            </Grid>

            <MDTypography variant="h6" mt={4} mb={2}>Parâmetros Dinâmicos (Para o Modelo)</MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Campanha (Para cálculos de descontos)</InputLabel>
                <MDSelect
                  isMulti={false}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.nome}
                  name="campanha"
                  placeholder="Nenhuma / Padrão"
                  value={campanhas.find(c => c.id === selectedCampanha) || null}
                  options={campanhas}
                  onChange={(option) => setSelectedCampanha(option ? option.id : "")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data de Retorno</InputLabel>
                <MDBox>
                  <MDInput
                    fullWidth
                    size="small"
                    type="date"
                    value={dataRetorno}
                    onChange={(e) => setDataRetorno(e.target.value)}
                  />
                </MDBox>
              </Grid>
            </Grid>

            <MDTypography variant="h6" mt={4} mb={2}>Anexos Adicionais</MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Anexar um arquivo genérico ao e-mail</InputLabel>
                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    component="label"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                  >
                    Fazer Upload
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                    />
                  </MDButton>
                  {attachment && (
                    <MDInput
                      size="small"
                      disabled
                      fullWidth
                      value={attachment.name}
                      sx={{ 
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: darkMode ? "#fff" : "#333"
                        }
                      }}
                    />
                  )}
                </MDBox>
              </Grid>
            </Grid>
            
            <Divider sx={{ mt: 4, mb: 2 }} />
            
            <MDTypography variant="h6" mb={2}>Teste de Disparo</MDTypography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  size="small"
                  label="E-mail de Teste"
                  value={emailTeste}
                  onChange={(e) => setEmailTeste(e.target.value)}
                  placeholder="Digite seu e-mail para teste"
                  autoComplete="new-password"
                  inputProps={{ autoComplete: 'new-password' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDButton
                  variant="gradient"
                  color="warning"
                  startIcon={<BugReportIcon />}
                  onClick={handleEnviarTeste}
                >
                  Enviar Teste
                </MDButton>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>visibility</Icon>}
                  onClick={handlePreview}
                >
                  Pré-visualizar Modelo
                </MDButton>
              </Grid>
            </Grid>

            <Divider sx={{ mt: 4, mb: 2 }} />
            
            <MDBox mt={2} display="flex" gap={2} justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="dark"
                startIcon={<DownloadIcon />}
                onClick={handleValidacao}
              >
                Gerar Planilha de Validação
              </MDButton>
              
              {runningBatchId ? (
                <MDButton
                  variant="gradient"
                  color="error"
                  startIcon={<Icon>cancel</Icon>}
                  onClick={handleCancelBatch}
                >
                  Cancelar Disparo em Andamento
                </MDButton>
              ) : (
                <MDButton
                  variant="gradient"
                  color="success"
                  startIcon={<SendIcon />}
                  onClick={handleEnviarLote}
                >
                  Disparar Lote Oficial ({recordsTotal} Casos)
                </MDButton>
              )}
            </MDBox>

            {batchStatusData && (
              <MDBox mt={3} p={2} bgColor={darkMode ? "dark" : "grey-100"} borderRadius="lg">
                <MDTypography variant="h6" color={darkMode ? "white" : "dark"} mb={1}>
                  Status do Lote: {batchStatusData.status}
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={2}>
                    <MDTypography variant="button" color="text" fontWeight="bold">Total de Linhas</MDTypography>
                    <MDTypography variant="h5" color={darkMode ? "white" : "dark"}>{batchStatusData.total}</MDTypography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDTypography variant="button" color="text" fontWeight="bold">Enviadas</MDTypography>
                    <MDTypography variant="h5" color="success">{batchStatusData.enviadosSucesso}</MDTypography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDTypography variant="button" color="text" fontWeight="bold">Com Erro</MDTypography>
                    <MDTypography variant="h5" color="error">{batchStatusData.erros}</MDTypography>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDTypography variant="button" color="text" fontWeight="bold">Faltantes</MDTypography>
                    <MDTypography variant="h5" color="warning">{batchStatusData.total - batchStatusData.processados}</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4} display="flex" alignItems="center" justifyContent="flex-end">
                    {batchStatusData.status === "CONCLUIDO" && batchStatusData.erros > 0 && (
                      <MDButton variant="gradient" color="error" size="small" onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/mala-direta/erros-lote/${batchStatusData.id}`, '_blank')}>
                        Baixar Relatório de Erros
                      </MDButton>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            )}
            
          </MDBox>
        </Card>
      </Grid>
      
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Pré-visualização do Modelo</DialogTitle>
        <Divider />
        <DialogContent>
          {previewLoading ? (
            <MDBox display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox sx={{ minHeight: "400px", padding: 2, border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#fff", color: "#000" }}>
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </MDBox>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <MDButton onClick={() => setPreviewOpen(false)} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} color="light">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmDialog.onConfirm} color="info" variant="gradient">
            Confirmar
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbarConfig.color}
        icon={snackbarConfig.icon}
        title={snackbarConfig.title}
        content={snackbarConfig.content}
        open={snackbarConfig.open}
        close={() => setSnackbarConfig({ ...snackbarConfig, open: false })}
        autoHideDuration={4000}
      />
    </Grid>
  );
}

export default MalaDiretaLote;
